import { useCallback, useEffect, useState } from 'react';
import { useAuth, useZooConfig } from '../lib/auth/hooks';
import {
  getPasskeyLabel,
  isPlatformAuthenticatorAvailable,
  isWebAuthnSupported,
  startPasskeyRegistration,
} from '../lib/auth/webauthn';
import { getSupabase } from '../lib/supabase/client';

// ─── Passkey Card ───

function PasskeyCard() {
  const { session, user } = useAuth();
  const { zooConfig } = useZooConfig();

  const [webauthnSupported] = useState(isWebAuthnSupported());
  const [platformAvailable, setPlatformAvailable] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<Array<{
    id: string;
    friendly_name: string | null;
    device_type: string;
    created_at: string;
    last_used_at: string | null;
  }> | null>(null);

  useEffect(() => {
    isPlatformAuthenticatorAvailable().then(setPlatformAvailable);
  }, []);

  // Load existing passkeys
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch after registration result changes
  useEffect(() => {
    const client = getSupabase();
    if (!client) return;
    client.rpc('get_my_passkeys').then(({ data, error: rpcError }) => {
      if (rpcError || !data) setCredentials([]);
      else setCredentials(data as typeof credentials);
    });
  }, [result]);

  const handleRegister = useCallback(async () => {
    if (!session?.access_token) return;
    setError(null);
    setResult(null);
    setRegistering(true);
    try {
      const { credentialId } = await startPasskeyRegistration(
        session.access_token,
        zooConfig?.orgId,
      );
      setResult(`Passkey registered! (${credentialId.slice(0, 12)}...)`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setRegistering(false);
    }
  }, [session, zooConfig]);

  const handleDelete = useCallback(async (credId: string) => {
    const client = getSupabase();
    if (!client) return;
    const { data } = await client.rpc('delete_passkey', { p_credential_id: credId });
    if (data) {
      setResult('Passkey deleted');
      // Also remove local private key if stored (Electron flow)
      localStorage.removeItem(`zbk3_passkey_${credId}`);
    } else {
      setError('Failed to delete passkey');
    }
  }, []);

  const passkeyLabel = getPasskeyLabel();

  return (
    <div
      style={{
        background: 'var(--color-bg-primary)',
        border: '1px solid var(--color-border-opaque)',
        borderRadius: 16,
        padding: 24,
        textAlign: 'left',
        width: '100%',
      }}
    >
      <h2
        style={{
          fontSize: 'var(--font-size-title4)',
          fontWeight: 600,
          color: 'var(--color-label-primary)',
          margin: '0 0 4px 0',
        }}
      >
        Passkeys
      </h2>
      <p
        style={{
          fontSize: 'var(--font-size-footnote)',
          color: 'var(--color-label-tertiary)',
          margin: '0 0 16px 0',
        }}
      >
        Sign in with {passkeyLabel.replace('Sign in with ', '')} instead of a password
      </p>

      {/* Status */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          fontSize: 'var(--font-size-caption)',
          color: 'var(--color-label-tertiary)',
        }}
      >
        <span
          style={{ color: webauthnSupported ? 'var(--color-tint-green)' : 'var(--color-tint-red)' }}
        >
          {webauthnSupported ? '✓ WebAuthn' : '✗ WebAuthn'}
        </span>
        <span
          style={{
            color: platformAvailable ? 'var(--color-tint-green)' : 'var(--color-tint-orange)',
          }}
        >
          {platformAvailable ? '✓ Biometric' : '✗ No biometric'}
        </span>
      </div>

      {/* Existing credentials */}
      {credentials && credentials.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {credentials.map((cred) => (
            <div
              key={cred.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                borderRadius: 10,
                backgroundColor: 'var(--color-fill-quaternary)',
                marginBottom: 8,
                fontSize: 'var(--font-size-footnote)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-label-secondary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4Z" />
                  <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
                </svg>
                <span style={{ color: 'var(--color-label-primary)', fontWeight: 500 }}>
                  {cred.friendly_name ?? `Passkey (${cred.device_type})`}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--color-label-tertiary)' }}>
                  {new Date(cred.created_at).toLocaleDateString()}
                </span>
                <button
                  type="button"
                  onClick={() => handleDelete(cred.id)}
                  aria-label="Delete passkey"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-tint-red)',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    fontSize: 'var(--font-size-caption)',
                    fontFamily: 'var(--font-family-primary)',
                    fontWeight: 500,
                    borderRadius: 4,
                    minHeight: 28,
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {credentials && credentials.length === 0 && (
        <p
          style={{
            fontSize: 'var(--font-size-footnote)',
            color: 'var(--color-label-tertiary)',
            margin: '0 0 16px 0',
            padding: 12,
            borderRadius: 10,
            backgroundColor: 'var(--color-fill-quaternary)',
            textAlign: 'center',
          }}
        >
          No passkeys registered yet
        </p>
      )}

      {/* Register button */}
      <button
        type="button"
        onClick={handleRegister}
        disabled={registering || !session || !webauthnSupported}
        style={{
          width: '100%',
          padding: '14px 16px',
          borderRadius: 10,
          border: 'none',
          backgroundColor: 'var(--color-tint-blue)',
          color: 'var(--color-label-emphasized)',
          fontSize: 'var(--font-size-body)',
          fontWeight: 600,
          fontFamily: 'var(--font-family-primary)',
          minHeight: 48,
          cursor: registering || !session || !webauthnSupported ? 'not-allowed' : 'pointer',
          opacity: registering || !session || !webauthnSupported ? 0.6 : 1,
          transition: 'opacity 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        {registering ? 'Registering...' : 'Add a passkey'}
      </button>

      {/* Feedback */}
      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            backgroundColor: 'var(--color-tint-light-red)',
            color: 'var(--color-tint-red)',
            fontSize: 'var(--font-size-footnote)',
          }}
        >
          {error}
        </div>
      )}
      {result && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 8,
            backgroundColor: 'var(--color-tint-light-green)',
            color: 'var(--color-tint-green)',
            fontSize: 'var(--font-size-footnote)',
          }}
        >
          {result}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard Page ───

export function DashboardPage() {
  const { user } = useAuth();

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '64px 24px', textAlign: 'center' }}>
      <h1
        style={{
          fontSize: 'var(--font-size-title1)',
          lineHeight: 'var(--line-height-title1)',
          letterSpacing: 'var(--letter-spacing-title1)',
          fontWeight: 700,
          color: 'var(--color-label-primary)',
          marginBottom: 8,
        }}
      >
        Welcome to Zoobook 3
      </h1>
      {user?.fullName && (
        <p
          style={{
            fontSize: 'var(--font-size-title4)',
            color: 'var(--color-label-secondary)',
            marginBottom: 16,
          }}
        >
          Hello, {user.fullName}
        </p>
      )}
      <p
        style={{
          color: 'var(--color-label-tertiary)',
          fontSize: 'var(--font-size-body)',
          lineHeight: 'var(--line-height-body)',
          marginBottom: 32,
        }}
      >
        Modular ERP for zoological gardens. Modules coming soon.
      </p>

      <PasskeyCard />

      {import.meta.env.DEV && (
        <a
          href="/dev"
          style={{
            display: 'inline-block',
            marginTop: 24,
            padding: '10px 20px',
            background: 'var(--color-tint-blue)',
            color: 'var(--color-static-white)',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-body)',
            textDecoration: 'none',
          }}
        >
          Developer Dashboard
        </a>
      )}
    </div>
  );
}
